import { Component, OnInit, ViewEncapsulation, inject } from "@angular/core";
import { MatTabsModule } from "@angular/material/tabs";
import { AssetManagementService } from "@app/assetmanagement/services/assetmanagement-data.service";
import { flattenOrgTree, toDisplayValue } from "@methods/CommonMethods";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { CerrixIconButtonComponent } from "@app/shared/molecules/cerrix-icon-button/cerrix-icon-button.component";
import { CerrixPageFooterComponent } from "@app/shared/cerrix-page-footer/cerrix-page-footer.component";
import { CerrixHeaderComponent } from "@app/shared/organisms/cerrix-header/cerrix-header.component";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { ModuleType } from "@enums/ModuleType";
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { CommonModule } from "@angular/common";
import { CerrixSlideOutEditItemComponent } from "@app/shared/organisms/cerrix-slide-out-edit-item/cerrix-slide-out-edit-item.component";
import { CerrixSlideOutEditItemMenuItems } from "@app/shared/organisms/cerrix-slide-out-edit-item/models/cerrix-slide-out-edit-item-menu-items.model";
import { EditAssetGeneralInformationComponent } from "@app/assetmanagement/components/edit-asset-general-information/edit-asset-general-information.component";
import { EditAssetOwnershipInformationComponent } from "@app/assetmanagement/components/edit-asset-ownership-information/edit-asset-ownership-information.component";
import { AssetUpdatePermissionsModel } from "@models/assetmanagement/AssetUpdatePermissionsModel";
import { EditAssetLifecycleInformationComponent } from "@app/assetmanagement/components/edit-asset-lifecycle-information/edit-asset-lifecycle-information.component";
import { ThirdPartyDataService } from "@pages/thirdparty/services/ThirdPartyDataService";
import { ThirdPartyContractDataService } from "@pages/thirdparty/services/ThirdPartyContractDataService";
import { firstValueFrom } from "rxjs";
import { MatMenuModule } from "@angular/material/menu";
import { AssetTabsHeaderComponent } from "@app/assetmanagement/components/asset-tabs-header/asset-tabs-header.component";
import { AssetDocumentHyperlinkToggleComponent } from "@app/assetmanagement/components/asset-document-hyperlink-toggle/asset-document-hyperlink-toggle.component";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { HttpClient } from "@angular/common/http";
import { AssetOwnerSelectorComponent } from "@app/assetmanagement/components/asset-owner-selector/asset-owner-selector.component";

@Component({
    selector: "app-assetmanagement-detail-view",
    templateUrl: "./assetmanagement-detail-view.component.html",
    styleUrls: ["./assetmanagement-detail-view.component.scss"],
    imports: [
        CommonModule,
        CerrixPageFooterComponent,
        CerrixIconButtonComponent,
        CerrixHeaderComponent,
        CerrixSlideOutEditItemComponent,
        MenuSystemDirective,
        TranslocoPipe,
        EditAssetGeneralInformationComponent,
        EditAssetLifecycleInformationComponent,
        EditAssetOwnershipInformationComponent,
        MatTabsModule,
        MatMenuModule,
        AssetTabsHeaderComponent,
        AssetDocumentHyperlinkToggleComponent,
        AssetOwnerSelectorComponent,
    ],
    providers: [CerrixInputValidationRegistryService, provideTranslocoScope("assetmanagement")],
    encapsulation: ViewEncapsulation.None,
})
export class AssetManagementDetailViewComponent implements OnInit {
    tab: TabModel;
    id: string;

    protected model: AssetManagementModel;

    protected breadcrumbs: string[] = [];

    protected canEdit = true;
    protected selectedTab = "overview";

    public organizationOwnerList: { label: string; value: string }[];

    protected editActive = false;

    protected thirdPartyName: string | null = null;
    protected contractNumber: string | null = null;
    protected users: IdGuidNameCombination[] = [];

    private updatePermissions: AssetUpdatePermissionsModel;

    private _ds = inject(AssetManagementService);
    private _transloco = inject(TranslocoService);
    private _standingDataDS = inject(StandingdataDataService);
    private _thirdPartyDs = inject(ThirdPartyDataService);
    private _contractDs = inject(ThirdPartyContractDataService);
    private _http = inject(HttpClient);

    protected sections: CerrixSlideOutEditItemMenuItems;

    async ngOnInit() {
        if (!this.id) {
            this.tab.close();
        }

        // Ensure the scope JSON is loaded before the synchronous translate() calls below,
        // otherwise they resolve to the raw key while the lazy scope is still loading.
        await firstValueFrom(
            this._transloco.load(`assetmanagement/${this._transloco.getActiveLang()}`),
        );

        await this.loadPermissions(this.id);

        if (this.canEdit) {
            this.loadSections();
        }

        this.breadcrumbs = [
            this._transloco.translate("assetmanagement.detail.breadcrumbWorkspace"),
        ];

        const asset = await firstValueFrom(this._ds.getAssetByIdVbeta(this.id));
        this.model = Object.assign(new AssetManagementModel(), asset, {
            thirdPartyInformation: {
                thirdPartyId: asset.thirdPartyInformation?.thirdPartyId,
                contractId: asset.thirdPartyInformation?.contractId,
            },
            ownershipInformation: {
                ownerId: asset.ownershipInformation?.ownerId,
                owningOrganizationId: asset.ownershipInformation?.owningOrganizationId,
            },
        });
        this.breadcrumbs.push(this.model.reference);

        const organizationsList: CerrixTreeItem[] = await firstValueFrom(
            this._standingDataDS.getOrganizationsIncludingSelected(
                ModuleType.AssetManagement,
                this.model.ownershipInformation.owningOrganizationId,
                false,
            ),
        );

        this.organizationOwnerList = flattenOrgTree(organizationsList);

        this.users = await this.getUsers();

        await Promise.all([this.loadThirdPartyName(), this.loadContractNumber()]);

        this.tab.showLoader = false;
    }

    private async getUsers(): Promise<IdGuidNameCombination[]> {
        if (!this.model.ownershipInformation.ownerId) {
            return [];
        }

        const params = `?selected=${this.model.ownershipInformation.ownerId}`;

        return await firstValueFrom(
            this._http.get<IdGuidNameCombination[]>(`/api/users/selected` + params),
        );
    }

    protected async onHyperlinksChanged(updated: AssetManagementModel): Promise<void> {
        this.model = updated;
    }

    public edit(): void {
        if (this.canEdit) {
            this.editActive = true;
        }
    }

    public closeCancel(): void {
        this.editActive = false;
    }

    public async onModelChange(updated: AssetManagementModel): Promise<void> {
        const prevThirdPartyId = this.model?.thirdPartyInformation?.thirdPartyId;
        const prevContractId = this.model?.thirdPartyInformation?.contractId;
        this.model = updated;
        if (updated.thirdPartyInformation?.thirdPartyId !== prevThirdPartyId) {
            await this.loadThirdPartyName();
        }
        if (updated.thirdPartyInformation?.contractId !== prevContractId) {
            await this.loadContractNumber();
        }
    }

    public getOrgOwnerName(orgOwnerGuid: string): string {
        return this.organizationOwnerList.find((org) => org.value === orgOwnerGuid)?.label;
    }

    private async loadThirdPartyName(): Promise<void> {
        if (!this.model.thirdPartyInformation?.thirdPartyId) {
            this.thirdPartyName = null;
            return;
        }
        try {
            const thirdPartyMetadata = await firstValueFrom(
                this._thirdPartyDs.getThirdPartiesMetaData([
                    this.model.thirdPartyInformation.thirdPartyId,
                ]),
            );
            const tp = thirdPartyMetadata?.find(
                (tp) => tp.id === this.model.thirdPartyInformation.thirdPartyId,
            );

            this.thirdPartyName = tp ? (toDisplayValue(tp.name, tp.archived) ?? null) : null;
        } catch {
            this.thirdPartyName = null;
        }
    }

    private async loadContractNumber(): Promise<void> {
        if (!this.model.thirdPartyInformation?.contractId) {
            this.contractNumber = null;
            return;
        }
        try {
            const idsToInclude = this.model.thirdPartyInformation?.contractId
                ? [this.model.thirdPartyInformation?.contractId]
                : [];
            const contracts = await firstValueFrom(
                this._contractDs.getContractsMetadata(
                    this.model.thirdPartyInformation.thirdPartyId,
                    idsToInclude,
                ),
            );
            const contract = contracts?.find(
                (c) => c.id === this.model.thirdPartyInformation.contractId,
            );
            this.contractNumber = contract
                ? toDisplayValue(contract.name, contract.archived)
                : null;
        } catch {
            this.contractNumber = null;
        }
    }

    private async loadPermissions(id: string): Promise<void> {
        const permissions = await firstValueFrom(this._ds.getPermissionsVbeta(id));
        this.updatePermissions = new AssetUpdatePermissionsModel(permissions.permissions);
        this.canEdit = permissions.permissions.length !== 0;
    }

    private loadSections(): void {
        const sections: CerrixSlideOutEditItemMenuItems = {
            items: [],
            activeItem: "general",
        };

        if (this.updatePermissions.canUpdateMetadata) {
            const metadataSection = {
                name: this._transloco.translate("assetmanagement.edit-general-information.title"),
                id: "general",
            };
            sections.items.push(metadataSection);
        }

        if (this.updatePermissions.canUpdateMetadata) {
            const metadataSection = {
                name: this._transloco.translate("assetmanagement.edit-lifecycle-information.title"),
                id: "lifecycle",
            };
            sections.items.push(metadataSection);
        }

        if (this.updatePermissions.canUpdateOwnership) {
            const ownershipSection = {
                name: this._transloco.translate("assetmanagement.edit-ownership-information.title"),
                id: "ownership",
            };
            sections.items.push(ownershipSection);
        }

        sections.activeItem = sections.items.first().id;

        this.sections = sections;
    }
}
